import React from 'react';
import { Patient } from '../../types/api';
import { Diagnosis } from '../../types/dispense';
import {
  COMMON_ACUTE_DIAGNOSES, ACUTE_CATEGORIES, ALL_ACUTE_DIAGNOSES, CHRONIC_DIAGNOSES
} from '../../constants/diagnoses';

interface DiagnosisModalProps {
  show: boolean;
  selectedAcute: string[];
  selectedChronic: string[];
  diagnosisSearch: string;
  diagnosisTab: 'common' | 'all' | 'chronic';
  expandedCategories: string[];
  patientDetails: Patient;
  setShow: (show: boolean) => void;
  setSelectedAcute: React.Dispatch<React.SetStateAction<string[]>>;
  setSelectedChronic: React.Dispatch<React.SetStateAction<string[]>>;
  setDiagnosisSearch: (search: string) => void;
  setDiagnosisTab: (tab: 'common' | 'all' | 'chronic') => void;
  toggleAcute: (code: string) => void;
  toggleChronic: (code: string) => void;
  toggleCategory: (label: string) => void;
  filterDiagnoses: (items: Diagnosis[]) => Diagnosis[];
  onConfirmSave: () => Promise<void>;
  /** Optional content above the actions (e.g. the HSS panel). */
  extraContent?: React.ReactNode;
}

export function DiagnosisModal({
  show, selectedAcute, selectedChronic, diagnosisSearch, diagnosisTab,
  expandedCategories, patientDetails,
  setShow, setDiagnosisSearch, setDiagnosisTab,
  toggleAcute, toggleChronic, toggleCategory, filterDiagnoses, onConfirmSave, extraContent
}: DiagnosisModalProps) {
  if (!show) return null;

  const handleClose = () => {
    setShow(false);
    setDiagnosisSearch('');
    setDiagnosisTab('common');
  };

  return (
    <div className="modal-overlay">
      <div className="modal modal-large" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 800, maxHeight: '90vh', display: 'flex', flexDirection: 'column' }}>
        <div className="modal-header" style={{ flexShrink: 0 }}>
          <div className="modal-title">Select Diagnosis</div>
          <button className="modal-close" onClick={handleClose}>&times;</button>
        </div>
        <p className="modal-subtitle" style={{ padding: '0 24px', margin: '0 0 8px 0', fontSize: 13, color: '#666' }}>
          Select diagnosis for this visit before saving. Green = selected by you.
        </p>

        {/* Search bar */}
        <div style={{ padding: '0 24px 8px', flexShrink: 0 }}>
          <input
            type="text"
            placeholder="Search diagnosis by name or ICD code..."
            value={diagnosisSearch}
            onChange={(e) => setDiagnosisSearch(e.target.value)}
            style={{ width: '100%', padding: '10px 14px', borderRadius: 8, border: '1px solid #d0d5dd', fontSize: 14, outline: 'none' }}
          />
        </div>

        {/* Tabs */}
        <div style={{ display: 'flex', gap: 0, padding: '0 24px 12px', borderBottom: '1px solid #e0e0e0', flexShrink: 0 }}>
          {([
            { key: 'all' as const, label: 'All Categories' },
            { key: 'common' as const, label: 'Common Acute' },
            { key: 'chronic' as const, label: 'Chronic' },
          ]).map(tab => (
            <button
              key={tab.key}
              onClick={() => setDiagnosisTab(tab.key)}
              style={{
                flex: 1, padding: '8px 12px', fontSize: 13, fontWeight: diagnosisTab === tab.key ? 700 : 500,
                background: diagnosisTab === tab.key ? 'var(--primary)' : 'transparent',
                color: diagnosisTab === tab.key ? '#fff' : '#333',
                border: `1px solid ${diagnosisTab === tab.key ? 'var(--primary)' : '#d0d5dd'}`,
                borderRadius: tab.key === 'all' ? '8px 0 0 8px' : tab.key === 'chronic' ? '0 8px 8px 0' : '0',
                cursor: 'pointer', transition: 'all 0.15s ease',
              }}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {/* Scrollable content */}
        <div style={{ flex: 1, overflowY: 'auto', padding: '12px 24px' }}>

          {/* Common Acute tab */}
          {diagnosisTab === 'common' && (
            <div className="diagnosis-section">
              <div className="diagnosis-label" style={{ fontSize: 14, fontWeight: 600, marginBottom: 8 }}>Common Acute Conditions</div>
              <div className="diagnosis-chips" style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                {filterDiagnoses(COMMON_ACUTE_DIAGNOSES).map(d => (
                  <button
                    key={d.code}
                    className={`diagnosis-chip ${selectedAcute.includes(d.code) ? 'selected' : ''}`}
                    onClick={() => toggleAcute(d.code)}
                    title={d.code}
                    style={{ fontSize: 12, padding: '6px 10px', borderRadius: 6 }}
                  >
                    {d.name}
                  </button>
                ))}
              </div>
              {filterDiagnoses(COMMON_ACUTE_DIAGNOSES).length === 0 && (
                <div style={{ color: '#999', fontSize: 13, padding: 8 }}>No matching diagnoses found. Try "All Categories" tab.</div>
              )}
            </div>
          )}

          {/* All Categories tab */}
          {diagnosisTab === 'all' && (
            <>
              {ACUTE_CATEGORIES.map(cat => {
                const filtered = filterDiagnoses(cat.items);
                if (filtered.length === 0) return null;
                const isExpanded = expandedCategories.includes(cat.label) || diagnosisSearch.trim().length > 0;
                const selectedCount = cat.items.filter(d => selectedAcute.includes(d.code)).length;
                return (
                  <div key={cat.label} style={{ marginBottom: 8 }}>
                    <button
                      onClick={() => toggleCategory(cat.label)}
                      style={{
                        width: '100%', display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                        padding: '8px 12px', background: selectedCount > 0 ? '#e8f5e9' : '#f5f5f5',
                        border: `1px solid ${selectedCount > 0 ? '#81c784' : '#e0e0e0'}`,
                        borderRadius: isExpanded ? '8px 8px 0 0' : 8,
                        cursor: 'pointer', fontSize: 13, fontWeight: 600, color: '#333',
                      }}
                    >
                      <span>{cat.label} ({cat.items.length})</span>
                      <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        {selectedCount > 0 && <span style={{ background: '#4caf50', color: '#fff', borderRadius: 10, padding: '2px 8px', fontSize: 11 }}>{selectedCount} selected</span>}
                        <span style={{ fontSize: 16 }}>{isExpanded ? '▾' : '▸'}</span>
                      </span>
                    </button>
                    {isExpanded && (
                      <div style={{ border: '1px solid #e0e0e0', borderTop: 'none', borderRadius: '0 0 8px 8px', padding: 8, background: '#fff' }}>
                        <div className="diagnosis-chips" style={{ display: 'flex', flexWrap: 'wrap', gap: 5 }}>
                          {filtered.map(d => (
                            <button
                              key={`${cat.label}-${d.code}`}
                              className={`diagnosis-chip ${selectedAcute.includes(d.code) ? 'selected' : ''}`}
                              onClick={() => toggleAcute(d.code)}
                              title={d.code}
                              style={{ fontSize: 11, padding: '5px 9px', borderRadius: 5 }}
                            >
                              {d.name}
                            </button>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </>
          )}

          {/* Chronic tab */}
          {diagnosisTab === 'chronic' && (
            <div className="diagnosis-section">
              <div className="diagnosis-label" style={{ fontSize: 14, fontWeight: 600, marginBottom: 8 }}>
                Chronic Conditions
                {(patientDetails.chronicConditionCodes?.length || patientDetails.chronicConditions.length) > 0 && (
                  <span style={{ fontSize: 11, fontWeight: 400, color: '#888', marginLeft: 8 }}>
                    (Light yellow = previously tagged)
                  </span>
                )}
              </div>
              <div className="diagnosis-chips" style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                {filterDiagnoses(CHRONIC_DIAGNOSES).map(d => {
                  const isSelected = selectedChronic.includes(d.code);
                  const isPreviouslyTagged = !isSelected && (
                    patientDetails.chronicConditionCodes?.includes(d.code) ||
                    (!patientDetails.chronicConditionCodes?.length && patientDetails.chronicConditions.some(
                      c => d.name.toLowerCase().includes(c.toLowerCase()) || c.toLowerCase().includes(d.name.split(' ')[0].toLowerCase())
                    ))
                  );
                  return (
                    <button
                      key={d.code}
                      className={`diagnosis-chip chronic ${isSelected ? 'selected' : ''}`}
                      onClick={() => toggleChronic(d.code)}
                      title={`${d.code}${isPreviouslyTagged ? ' (previously tagged)' : ''}`}
                      style={{
                        fontSize: 12, padding: '6px 10px', borderRadius: 6,
                        ...(isPreviouslyTagged ? { background: '#fffde7', borderColor: '#fbc02d' } : {})
                      }}
                    >
                      {d.name}
                    </button>
                  );
                })}
              </div>
              {filterDiagnoses(CHRONIC_DIAGNOSES).length === 0 && (
                <div style={{ color: '#999', fontSize: 13, padding: 8 }}>No matching chronic diagnoses found.</div>
              )}
            </div>
          )}
        </div>

        {/* Selected summary */}
        <div style={{ flexShrink: 0, borderTop: '1px solid #e0e0e0', padding: '12px 24px', background: '#fafafa' }}>
          <div className="selected-diagnosis-summary" style={{ fontSize: 13, marginBottom: 12 }}>
            <strong>Selected ({selectedAcute.length + selectedChronic.length}):</strong>{' '}
            {[
              ...selectedAcute.map(c => {
                const d = ALL_ACUTE_DIAGNOSES.find(x => x.code === c);
                return d ? d.name : c;
              }),
              ...selectedChronic.map(c => {
                const d = CHRONIC_DIAGNOSES.find(x => x.code === c);
                return d ? `${d.name} (CHR)` : c;
              })
            ].join(', ') || 'None'}
          </div>

          {extraContent}

          <div className="modal-actions" style={{ display: 'flex', gap: 12 }}>
            <button className="btn btn-secondary" onClick={handleClose}>Cancel</button>
            <button className="btn btn-success" onClick={onConfirmSave}>Confirm & Save</button>
          </div>
        </div>
      </div>
    </div>
  );
}
